<template>
  <div
    :class="[
      $style.bonusPromoBlock,
      {
        [$style.visibleCategoryFilterMobile]: storeMenuStatuses.isFilterShown,
      },
    ]"
  >
    <div :class="$style.promotionsWrapperTitleBlock">
      <span :class="[$style.promotionsWrapperTitle]">
        {{ data.promotionsTitle }}
      </span>
      <BitLinkButton
        v-if="storeSsPlayer.isPlayerAuthorised"
        tag="button"
        @click.prevent="openHistoryBonusModal"
      >
        {{ data.bonusHistoryTitle }}
      </BitLinkButton>
    </div>
    <template v-if="!isLoading">
      <div v-if="!checkAbuserGroup" :class="$style.promotionsWrapper">
        <div
          v-if="playerSsBonuses.usableBonusesAndFreespins.length"
          :class="$style.activeBonusesBlock"
        >
          <p :class="$style.activeBonusesTitle">{{ data.myBonusesTitle }}</p>
          <div :class="$style.activeBonusesHolder">
            <ActivePromotionCard
              v-for="item in playerSsBonuses.usableBonusesAndFreespins"
              :key="item.id"
              :item="item"
            />
          </div>
        </div>
      </div>
      <!-- <pre style="color: white">
        {{ promotionsToShow }}
      </pre> -->
      <div :class="$style.promotionSubTitleHolder">
        <div :class="$style.promotionSubTitle">
          {{ bonusesData.bonusSubTitle }}
        </div>
        <div v-if="!isMobileLayout" :class="$style.categoryFilter">
          <template v-if="form.category && form.category !== bonusesData.allPromoTabTitle">
            <BitLinkButton :class="$style.categoryFilterTitle" @click="resetCategory">
              {{ bonusesData.promoSelectTitle }}
            </BitLinkButton>
          </template>
          <BitSelect
            v-model="form.category"
            :options="selectOptions"
            :default-value="bonusesData.allPromoTabTitle"
            :class="$style.categoryFilterSelect"
            @update:modelValue="onSelectCategory"
          >
            <template #appendInputIcon>
              <img src="/images/common/providerSearch.svg?url_encode" alt="providerSearch" />
            </template>
          </BitSelect>
        </div>
        <div v-if="isMobileLayout" :class="$style.categoryFilterMobileBlock">
          <BitTabButton is-with-external-icon @click="storeMenuStatuses.toggleFilterMenu()">
            <img src="/images/common/providerSearch.svg?url_encode" alt="providerSearch" />
          </BitTabButton>
          <div
            v-if="storeMenuStatuses.isFilterShown"
            v-click-outside="closeCategoryFilterMobile"
            :class="$style.categoryFilterMobile"
          >
            <BitBottomSheetHeader
              :class="$style.categoryFilterMobileHead"
              :title="storeComponents.search.searchFilterProviderTitle"
              @close="closeCategoryFilterMobile"
            />
            <div :class="$style.categoryFilterMobileContent">
              <ul :class="$style.customCategoryList">
                <BitSelectItem
                  v-for="tab in selectOptions"
                  :key="tab"
                  :option-text="tab"
                  :is-selected="form.category === tab"
                  @click="onSelectCategory(tab)"
                />
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasPromotionsToShow" :class="$style.promotionCardWrapper">
        <div
          v-for="(bonus, index) in promotionsToShow"
          :key="bonus.bonusId"
          :class="$style.promotionCardWrapperItem"
        >
          <BonusPromotionCard
            v-gtm="{
              event: 'bonus_open_card',
              event_label: bonus.title || bonus.offer,
              tab_promo: activeTab,
            }"
            :index="index"
            :bonus="bonus"
            :active-tab="activeTab"
          />
        </div>
      </div>
      <div v-else :class="$style.promotionCardNotFound">
        <img
          :class="$style.noTransactionHistoryBg"
          src="/images/common/NoBonuses.svg?url_encode"
          alt="NoBonuses"
        />
        <Markdown
          :class="$style.transactionHistoryGlobalText"
          :text="storeHistoryModal.bonusHistoryNoticeText"
        />
      </div>

      <div
        v-if="comingSoonBonuses.length >= 1"
        :class="[$style.promotionSubTitle, $style.promotionSubTitleHolder]"
      >
        {{ bonusesData.bonusSubTitleComingSoon }}
      </div>

      <div v-if="comingSoonBonuses.length >= 1" :class="[$style.promotionCardWrapper]">
        <div
          v-for="(bonus, index) in comingSoonBonuses"
          :key="bonus.bonusId"
          :class="$style.promotionCardWrapperItem"
        >
          <BonusPromotionCard :index="index" :bonus="bonus" :is-coming-soon-mode="true" />
        </div>
      </div>
    </template>
    <BonusPromoBlockSkeleton v-else />
  </div>
</template>

<script lang="ts" setup>
import {
  BitBottomSheetHeader,
  BitLinkButton,
  BitSelect,
  BitSelectItem,
  BitTabButton,
} from '@bit/ui-kit';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import ActivePromotionCard from '~/components/bonus/activePromotionCard.vue';
import BonusPromotionCard from '~/components/bonus/promotionCard.vue';
import BonusPromoBlockSkeleton from '~/components/infoPage/bonusPromoBlockSkeleton.vue';
import Markdown from '~/components/markdown.vue';
import { useStorePages } from '~/stores/abstract/pages';
import { useStoreCrmBonus } from '~/stores/backend/crm-bonus';
import { useStoreMenuStatuses } from '~/stores/client/menuStatuses';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreSsPlayerBonuses } from '~/stores/softswiss/playerBonuses';
import { useStoreBonuses } from '~/stores/strapi/bonuses';
import { useStoreBonusesTabs } from '~/stores/strapi/bonusesTabs';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreModalTransactionHistory } from '~/stores/strapi/modals/transactionHistory';
import { EBonusTabTypesExtension, EBonusType, type TBonusTab } from '~/types/bonuses';
import { eHistoryTab, SettingsId } from '~/types/settings';
import { BONUS_ABUSER_GROUP } from '~/utils/constants';
import clickOutside from '~/utils/directives/vClickOutside';
import type { ComponentInfoPageBlocksPromoBonusBlock } from '~/types/infoPages';

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksPromoBonusBlock>,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();
const form = reactive({
  category: '',
});
const vClickOutside = clickOutside;
const storeBonuses = useStoreBonuses();
const storeSsPlayer = useStoreSsPlayer();
const storeBonusesTabs = useStoreBonusesTabs();
const storeComponents = useStoreComponents();
const storeCrmBonus = useStoreCrmBonus();
const platform = usePlatform();
const storeMenuStatuses = useStoreMenuStatuses();
const playerSsBonuses = useStoreSsPlayerBonuses();
const storeHistoryModal = useStoreModalTransactionHistory();

const isLoadingStrapiBonuses = ref(true);
const isLoadingSsBonuses = ref(true);

const closeCategoryFilterMobile = () => {
  storeMenuStatuses.toggleFilterMenu(false);
};

const activeTab = ref<TBonusTab>(EBonusTabTypesExtension.All);

const tabsToShow = computed(() =>
  storeBonusesTabs.tabs.filter((tab) => storeBonuses.promotionBonusesByType[tab.bonusType]?.length)
);

const isLoading = computed(() => isLoadingStrapiBonuses.value || isLoadingSsBonuses.value);

const isMobileLayout = computed(() => platform.isMobile);

const promotionsToShow = computed(() =>
  activeTab.value === EBonusTabTypesExtension.All
    ? storeBonuses.bonusesWithStatusesWithoutAchievements.filter(
        (bonus) => bonus.status !== 'locked'
      )
    : storeBonuses.promotionBonusesByType[activeTab.value]?.filter(
        (bonus) => bonus.status !== 'locked'
      ) || []
);

const hasPromotionsToShow = computed(() => promotionsToShow.value.length > 0);

const bonusesData = computed(() => storeComponents.bonuses);

const comingSoonBonuses = computed(() =>
  storeBonuses.bonusesWithStatusesWithoutAchievements.filter((bonus) => bonus.status === 'locked')
);

const checkAbuserGroup = computed(() => {
  return storeSsPlayer.playerGroupIds.includes(BONUS_ABUSER_GROUP);
});

const setActiveTab = (type: TBonusTab) => {
  activeTab.value = type;
};

const selectOptions = computed(() => [
  bonusesData.value.allPromoTabTitle,
  ...tabsToShow.value.map((tab) => tab.title),
]);

const onSelectCategory = (selectedTitle: string) => {
  closeCategoryFilterMobile();
  form.category = selectedTitle;

  if (selectedTitle === bonusesData.value.allPromoTabTitle) {
    setActiveTab(EBonusTabTypesExtension.All);
    return;
  }

  const selectedTab = tabsToShow.value.find((tab) => tab.title === selectedTitle);
  if (selectedTab) {
    setActiveTab(selectedTab.bonusType);
  }
};

const resetCategory = () => {
  form.category = bonusesData.value.allPromoTabTitle;
  setActiveTab(EBonusTabTypesExtension.All);
};

const openHistoryBonusModal = () => {
  useStorePages().openAccountSettingsPage(SettingsId.History, `?tab=${eHistoryTab.Bonuses}`);
};

watch(
  () => activeTab.value,
  (val) => {
    const selectedTab = tabsToShow.value.find((tab) => tab.bonusType === val);
    if (val === EBonusTabTypesExtension.All) {
      form.category = bonusesData.value.allPromoTabTitle;
      return;
    }
    if (selectedTab) {
      form.category = selectedTab.title;
    }
  }
);

watch(
  () => activeTab.value,
  (val) => {
    if (val === EBonusTabTypesExtension.All) {
      router.push({
        path: route.fullPath,
        query: { ...route.query, tab: undefined },
      });
    } else {
      router.push({
        path: route.fullPath,
        query: { ...route.query, tab: val },
      });
    }
  }
);

watch(
  () => route.query.tab,
  (val) => {
    if (val) {
      activeTab.value = val as EBonusType;
    }
  }
);

onMounted(async () => {
  isLoadingStrapiBonuses.value = true;
  isLoadingSsBonuses.value = true;
  await storeBonuses.loadBonusesPageData();

  if (storeSsPlayer.isPlayerAuthorised || storeSsPlayer.hasAuthToken) {
    await playerSsBonuses.loadAllBonuses(true);
    isLoadingSsBonuses.value = false;
  } else {
    isLoadingSsBonuses.value = false;
  }

  storeCrmBonus.loadActiveBonuses();
  const tabQuery = route.query.tab as EBonusType;
  const isValidTabQuery = Object.values(EBonusType).includes(tabQuery);
  if (tabQuery && isValidTabQuery) {
    activeTab.value = tabQuery;
    isLoadingStrapiBonuses.value = false;
  }
  isLoadingStrapiBonuses.value = false;
});
</script>

<style lang="scss" module src="./bonusPromoBlock.scss"></style>
